import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search, FilterList, Error, Pending } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomerSelectionDialog from '../../components/CustomerSelectionDialog';
import { GetOrdersVm } from 'api/picking/get-orders.vm';
import { getOrders } from 'api/picking/get-orders';
import { OrderVm, OrdersStatus } from 'api/picking/order.vm';

const SalesOrdersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<OrderVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [clientName, setClientName] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  // Estado local para inputs do formulário
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [clientFilter, setClientFilter] = useState(searchParams.get('client') || '');
  const [statusFilter, setStatusFilter] = useState<OrdersStatus | null>((searchParams.get('status') as OrdersStatus | null) || null);

  const updateSearchParams = (newParams: { search?: string; client?: string; status?: OrdersStatus | null }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.search !== undefined) {
      newParams.search ? params.set('search', newParams.search) : params.delete('search');
    }
    if (newParams.client !== undefined) {
      newParams.client ? params.set('client', newParams.client) : params.delete('client');
    }
    if (newParams.status !== undefined) {
      newParams.status ? params.set('status', newParams.status) : params.delete('status');
    }

    setSearchParams(params);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const fetchOrders = (
    offset: string | null,
    reset: boolean = false,
    search: string = searchTerm,
    client: string = clientFilter,
    status: OrdersStatus | null = statusFilter
  ) => {
    setLoading(true);

    getOrders(offset || '', search, client, status, {
      success: (response: GetOrdersVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setOrders(reset ? response.objects : [...orders, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar encomendas:', err);
        setLoading(false);
      },
    });
  };

  // Carregamento inicial
  useEffect(() => {
    fetchOrders(null, true, searchTerm, clientFilter, statusFilter);
    // eslint-disable-next-line
  }, []);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchOrders(nextOffset);
  };

  const handleSearchSubmit = () => {
    updateSearchParams({
      search: searchTerm,
      client: clientFilter,
      status: statusFilter,
    });
    fetchOrders(null, true);
  };

  const handleStatusFilter = (status: OrdersStatus | null) => {
    setStatusFilter(status);
    handleFilterClose();
  };

  const handleClientSelect = (clientId: string, clientName: string) => {
    setClientFilter(clientId);
    setClientName(clientName);
    setClientDialogOpen(false);
  };

  const handleClearClientFilter = () => {
    setClientFilter('');
    setClientName('');
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleRowClick = (orderId: string) => {
    navigate(`/shipping/sales-orders/${encodeURIComponent(orderId)}?${searchParams.toString()}`);
  };

  const getStatusChip = (order: OrderVm) => {
    switch (order.status) {
      case OrdersStatus.PENDING:
        return <Chip icon={<Pending fontSize="small" />} label="Pendente" color="info" size="small" />;
      case OrdersStatus.CLOSED:
        return <Chip icon={<Error fontSize="small" />} label="Fechada" color="error" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Encomendas de Cliente
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <TextField
              size="small"
              placeholder="Procurar encomendas..."
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
              }}
              sx={{ width: 300 }}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {clientName ? (
                <Chip label={clientName} onDelete={handleClearClientFilter} sx={{ mr: 1 }} />
              ) : (
                <Button variant="outlined" onClick={() => setClientDialogOpen(true)} sx={{ mr: 1 }}>
                  Filtrar por Cliente
                </Button>
              )}
            </Box>

            <IconButton aria-label="filter" onClick={handleFilterClick} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <FilterList />
            </IconButton>
          </Box>

          <Button variant="contained" onClick={handleSearchSubmit} disabled={loading} sx={{ ml: 'auto' }}>
            Procurar
          </Button>
        </Box>

        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
          <MenuItem onClick={() => handleStatusFilter(null)}>Todos os Estados</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(OrdersStatus.PENDING)}>Pendente</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(OrdersStatus.CLOSED)}>Fechada</MenuItem>
        </Menu>
      </Paper>

      <CustomerSelectionDialog open={clientDialogOpen} onClose={() => setClientDialogOpen(false)} onSelect={handleClientSelect} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID da Encomenda</TableCell>
              <TableCell>Circuito</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Criada Em</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id} hover onClick={() => handleRowClick(order.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{order.id}</TableCell>
                <TableCell>{order.circuitName}</TableCell>
                <TableCell>{order.clientName}</TableCell>
                <TableCell>{new Date(order.createdAt).toLocaleDateString('pt-PT')}</TableCell>
                <TableCell>{getStatusChip(order)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : orders.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais encomendas para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma encomenda encontrada
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SalesOrdersPage;
